import debounce from 'just-debounce-it';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import FadeIn from '@/components/animations/FadeIn';
import TipTapEditorV3 from '@/components/TipTapEditorV3/TipTapEditorV3';
import { Input } from '@/components/ui/input';
import { EditorType } from '@/Enums/enum';
import useSaveDraft from '@/hooks/useSaveDraft';
import { useTabLabels } from '@/hooks/useTabLabels';
import { StyledH5 } from '@/Shared/Typography/typography';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CourseDiff from '../CourseDiff';
import CourseEditContext from '../CourseEditContext';

const CourseDescriptionTab = () => {
	const {
		saveDraft,
		canEdit,
		courseData,
		courseDraftData,
		isApprovalMode,
		updatePrereq,
		updateCourseReducer,
	} = useContext(CourseEditContext);

	const { updateCommentTypes } = useCourseEditStore();
	const labels = useTabLabels();

	const {
		course_description: courseDescription,
		course_extras: courseExtras,
		course_prereq: coursePrereq,
		course_id: courseId,
	} = courseData;

	const {
		course_description: courseDraftDescription,
		course_extras: courseDraftExtras,
		course_prereq: courseDraftPrereq,
	} = courseDraftData;

	const [isCommented, setIsCommented] = useState(false);
	const [prereqValue, setPrereqValue] = useState(courseDraftPrereq ?? '');
	// When true, the next courseDraftPrereq change came from the user typing — skip the effect
	// to avoid resetting the input value and jumping the cursor.
	const isUserTypingRef = useRef(false);
	// once a comment is added, state is updated and the draft is saved
	const saveAfterCommenting = (value: string, type: EditorType) => {
		updateCourseReducer(value, type);
		setIsCommented(!isCommented);
	};
	// once a comment is added, save a draft using hook
	useSaveDraft(isCommented, saveDraft, setIsCommented);

	const onDescriptionChange = debounce((value: string) => {
		updateCourseReducer(value, EditorType.DESCRIPTION);
	}, 500);

	const onPrereqChange = debounce((value: string) => {
		updatePrereq(value);
	}, 500);

	const onAdditionalInfoChange = debounce((value: string) => {
		updateCourseReducer(value, EditorType.EXTRAINFO);
	}, 500);

	useEffect(() => {
		updateCommentTypes([EditorType.DESCRIPTION, EditorType.EXTRAINFO]);
	}, [updateCommentTypes]);

	// Sync prereq input when an external update arrives (e.g. file import).
	// Skip if the change originated from the user typing to avoid cursor jumping.
	useEffect(() => {
		if (isUserTypingRef.current) {
			isUserTypingRef.current = false;
			return;
		}
		setPrereqValue(courseDraftPrereq ?? '');
	}, [courseDraftPrereq]);

	return (
		<FadeIn style={{ width: '100%' }}>
			<div>
				{!isApprovalMode && (
					<TipTapEditorV3
						content={courseDraftDescription}
						onChange={onDescriptionChange}
						placeholder="Add course description..."
						type={EditorType.DESCRIPTION}
						isEditable={canEdit}
						commentAddedCallback={(value) => saveAfterCommenting(value, EditorType.DESCRIPTION)}
						id={courseId}
					/>
				)}
				{isApprovalMode && (
					<CourseDiff
						title="Course Description"
						original={courseDescription}
						draft={courseDraftDescription}
					/>
				)}
				<StyledH5 mt="32px" mb="8px">
					{labels.prereq}
				</StyledH5>

				{!isApprovalMode && (
					<Input
						type="text"
						value={prereqValue}
						placeholder={labels.prereq}
						onChange={(e: React.FormEvent<HTMLInputElement>) => {
							const val = e.currentTarget.value;
							isUserTypingRef.current = true;
							setPrereqValue(val);
							onPrereqChange(val);
						}}
					/>
				)}
				<CourseDiff original={coursePrereq} draft={courseDraftPrereq} />
				<StyledH5 mt="32px" mb="8px">
					{labels.extraInfo}
				</StyledH5>
				{!isApprovalMode && (
					<TipTapEditorV3
						content={courseDraftExtras}
						onChange={onAdditionalInfoChange}
						placeholder="Add course additional information..."
						type={EditorType.EXTRAINFO}
						isEditable={canEdit}
						commentAddedCallback={(value) => saveAfterCommenting(value, EditorType.EXTRAINFO)}
						id={courseData.course_id}
					/>
				)}
				{isApprovalMode && <CourseDiff original={courseExtras} draft={courseDraftExtras} />}
			</div>
		</FadeIn>
	);
};

export default CourseDescriptionTab;
